// Cloudscape's built-in select/multiselect autofilter matches against
// ['value', 'label', 'description', 'labelTag'] (see node_modules/@cloudscape-design/
// components/internal/components/option/utils/filter-options.js `matchesSingleOption`).
// When `value` is a UUID (user/group pickers, tag selectors, etc.) typing a digit or
// short hex string matches the UUID contents and surfaces unrelated rows (RSP-5284).
// Pre-filter on visible fields only so invisible values can't leak into matches.

interface VisibleOption {
  value?: string;
  label?: string;
  description?: string;
  labelTag?: string;
  tags?: readonly string[];
  filteringTags?: readonly string[];
}

interface VisibleOptionGroup extends VisibleOption {
  options: readonly VisibleOption[];
}

type VisibleOptionOrGroup = VisibleOption | VisibleOptionGroup;

/** Minimum shape this filter can match against. */
export type FilterableOption = VisibleOptionOrGroup;

// Field separator for the flattened haystack. A control character no user can
// type, so it can never bridge two fields into a spurious match (searching
// "aliceops" must not match label "Alice" + tag "ops").
const FIELD_SEPARATOR = '\u0000';

// Lowercased searchable text per option, built once per option object.
//
// The naive form lowercases up to five fields per option on every keystroke —
// for a 12k-user owner picker that is ~60k throwaway strings per character
// typed. Options are stable objects (memoised upstream in
// useGroupAndUserOptions), so a WeakMap keyed on the option gives one
// allocation per option for the lifetime of that array, and each keystroke
// becomes a single `includes` per option. Entries are collected with the
// options themselves.
const haystackCache = new WeakMap<object, string>();

const buildHaystack = (option: VisibleOption): string => {
  const fields: string[] = [];
  if (option.label) {
    fields.push(option.label);
  }
  if (option.description) {
    fields.push(option.description);
  }
  if (option.labelTag) {
    fields.push(option.labelTag);
  }
  if (option.tags) {
    fields.push(...option.tags);
  }
  if (option.filteringTags) {
    fields.push(...option.filteringTags);
  }

  return fields.join(FIELD_SEPARATOR).toLowerCase();
};

const haystackFor = (option: VisibleOption): string => {
  const cached = haystackCache.get(option);
  if (cached !== undefined) {
    return cached;
  }
  const haystack = buildHaystack(option);
  haystackCache.set(option, haystack);

  return haystack;
};

/** `needle` must already be lowercased. */
const optionMatches = (option: VisibleOption, needle: string): boolean =>
  haystackFor(option).includes(needle);

// Group labels are matched on their own, without the group's children folded in
// — a group is kept whole when its own label matches, and otherwise only for the
// children that match.
const groupLabelMatches = (
  option: VisibleOptionGroup,
  needle: string
): boolean => !!option.label && option.label.toLowerCase().includes(needle);

const isGroup = (option: VisibleOptionOrGroup): option is VisibleOptionGroup =>
  'options' in option && Array.isArray((option as VisibleOptionGroup).options);

export const filterOptionsByVisibleText = <T extends VisibleOptionOrGroup>(
  options: readonly T[] | undefined,
  filteringText: string
): readonly T[] | undefined => {
  if (!options || !filteringText) {
    return options;
  }
  const needle = filteringText.toLowerCase();

  return options.reduce<T[]>((acc, option) => {
    if (isGroup(option)) {
      if (groupLabelMatches(option, needle)) {
        acc.push(option);

        return acc;
      }
      const childMatches = option.options.filter((child) =>
        optionMatches(child, needle)
      );
      if (childMatches.length > 0) {
        acc.push({ ...option, options: childMatches });
      }

      return acc;
    }
    if (optionMatches(option, needle)) {
      acc.push(option);
    }

    return acc;
  }, []);
};
